import React, { Component } from 'react'
import { findDOMNode } from 'react-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { reduxForm, Field } from 'redux-form'

import classNames from './ImageSearch.css'
import {
  selectQuery, removeQuery,
  addTerm, removeTerm, editTerm, updateTerm, updateOperator, updateRating,
  unexcludeAllImages, unincludeAllImages, removeAllCameras, resetLibraries,
  openAdvancedSearch, unfixAlbum, setRootProperty, clearEditingState, updateTargetDate
} from '../../redux/query'
import { fetchSearchOptions } from '../../redux/search'
import { fetchDynamicLinks } from '../../redux/dynlinks'
import { isEmptyQuery } from '../../utils/images'
import { pluralize, titleCase } from '../../utils/string'
import { userHasPerm } from '../../utils/auth'
import ImageSearchInput from './ImageSearchInput'
import OperatorButton from './OperatorButton'
import Icon from '../Icon'
import Stars from '../Stars'
import { DYNAMIC_ALBUMS, UNFIX_WARNING, DEFAULT_START_DATE } from '../../constants'

const CLOSE_ICON_SIZE = 18

class ImageSearch extends Component {
  componentDidMount () {
    const { options, fetchSearchOptions, dynlinks, fetchDynamicLinks } = this.props
    if (!options.keywords) {
      fetchSearchOptions()
    }
    if (!dynlinks) {
      fetchDynamicLinks()
    }
  }
  focusInput = () => {
    findDOMNode(this).querySelector('input').focus()
  }
  openCameraPicker = () => {
    this.props.openAdvancedSearch('camera')
  }
  openLibraries = () => {
    this.props.openAdvancedSearch('library')
  }
  openRating = () => {
    this.props.openAdvancedSearch('rating')
  }
  openLimit = () => {
    this.props.openAdvancedSearch('limit')
  }
  render () {
    const {
      className,
      query,
      options,
      dynlinks,
      selectQuery,
      removeQuery,
      addTerm,
      updateTerm,
      updateRating,
      removeTerm,
      setRootProperty,
      clearEditingState,
      updateTargetDate,
      updateOperator,
      unexcludeAllImages,
      unincludeAllImages,
      removeAllCameras,
      resetLibraries,
      userHasPerm
    } = this.props

    const activeQuery = query.structure.queries[query.selectedIndex]
    const isFixed = query.structure.fixed && query.structure.fixed.length !== 0
    const wrapperClass = isFixed ? ' ' + classNames.fixedWrapper : ''
    const queryRowClass = query.selectedIndex % 2 ? classNames.queryRowDarker : classNames.queryRow

    const onRemoveLimit = () => {
      setRootProperty('limit', undefined)
      setRootProperty('diversity', undefined)
    }

    return (
      <div className={className + wrapperClass}>
        {query.structure.queries.length > 1 &&
          <div className={queryRowClass}>
            <div className={classNames.queryLeft}>
              <button
                className={classNames.queryButton}
                onClick={() => selectQuery(query.selectedIndex - 1)}
                disabled={query.selectedIndex === 0}
                title='Previous query'>
                <Icon icon='chevron-left' />
              </button>
              <span className={classNames.queryTitle}>
                Query {query.selectedIndex + 1}/{query.structure.queries.length}
              </span>
              <button
                className={classNames.queryButton}
                onClick={() => selectQuery(query.selectedIndex + 1)}
                title='Next query'>
                  {query.selectedIndex === query.structure.queries.length - 1 &&
                    <Icon icon='plus-small' size={20} style={{ opacity: 0.5, marginLeft: '2px' }} />
                  }
                  {query.selectedIndex !== query.structure.queries.length - 1 &&
                    <Icon icon='chevron-right' />
                  }
              </button>
            </div>
            <OperatorButton
              className={classNames.operatorButton}
              onChange={value => updateOperator(value)}
              value={activeQuery.operator}
            />
            <button
              className={classNames.queryCloseButton}
              onClick={() => removeQuery(query.selectedIndex)}
              disabled={query.structure.queries.length === 1}
              title='Remove query'>
              <Icon icon='close' size={CLOSE_ICON_SIZE} />
            </button>
          </div>
        }
        <ul>
          {isFixed &&
            renderFixed(query.structure.fixed.length, this.props.unfixAlbum)
          }
          {!!(query.structure.limit || query.structure.diversity) &&
            renderLimit(query.structure.limit, query.structure.diversity, this.openLimit, onRemoveLimit)
          }
          {renderTerms(activeQuery, this.focusInput, this.props, this.openRating, options.keywordMap)}
          {activeQuery.camera.length !== 0 &&
            renderCameras(activeQuery.camera.length, this.openCameraPicker, removeAllCameras)
          }
          {activeQuery.library.length !== 0 && activeQuery.library.length !== query.libraries.length &&
            renderLibraries(activeQuery.library, this.openLibraries, resetLibraries)
          }
          {query.structure.include.length !== 0 &&
            renderIncludes(query.structure.include.length, unincludeAllImages)
          }
          {query.structure.exclude.length !== 0 &&
            renderExcludes(query.structure.exclude.length, unexcludeAllImages)
          }
        </ul>
        <Field
          component={ImageSearchInput}
          name='search'
          defaultValue={{}}
          props={{
            query,
            activeQuery,
            options,
            dynlinks,
            addTerm,
            updateTerm,
            updateRating,
            removeTerm,
            userHasPerm,
            setRootProperty,
            clearEditingState,
            updateTargetDate,
            autoFocus: this.props.autoFocus,
            focusInput: this.focusInput,
            libraries: query.libraries,
            addNewQuery: !isEmptyQuery(activeQuery) && (() => selectQuery(query.selectedIndex + 1)),
            disabled: isFixed
          }}
        />
      </div>
    )
  }
}

function renderTerms (activeQuery, focusInput, { removeTerm, editTerm, updateTerm, updateRating }, openRating, keywordMap) {
  const operatorTypes = [ 'freetext', 'keyword', 'tags', 'date', 'dynlink', 'path' ]
  return Object.keys(activeQuery).map(type => {
    if (type === 'library' || type === 'camera') {
      return null
    }
    if (activeQuery[type] instanceof Array === false) {
      return null
    }
    if (type === 'rating' && activeQuery.rating.length !== 0) {
      const updateOperator = value => {
        for (let i = 0; i !== activeQuery.rating.length; i++) {
          updateTerm({
            type,
            termIndex: i,
            value: {
              ...activeQuery.rating[i],
              operator: value
            }
          })
        }
      }
      return (
        <li className={classNames.termRow}>
          <button className={classNames.termButton} type='button' onClick={openRating}>
            {renderTermValue('rating', activeQuery.rating)}
          </button>
          <OperatorButton
            className={classNames.operatorButton}
            onChange={updateOperator}
            value={activeQuery.rating[0].operator}
          />
          <button
            className={classNames.queryCloseButton}
            type='button'
            onClick={() => updateRating([])}
            title='Remove search term'>
            <Icon icon='close' size={CLOSE_ICON_SIZE} />
          </button>
        </li>
      )
    }
    return activeQuery[type].map((term, termIndex) => {
      const showOperator = operatorTypes.indexOf(type) !== -1
      const onRemove = () => {
        removeTerm(type, termIndex)
        focusInput()
      }
      const onEdit = () => {
        editTerm(type, termIndex, type === 'keyword' ? getKeywordName(term, keywordMap) : term)
        if (type === 'freetext' || type === 'keyword') {
          focusInput()
        }
      }
      const editDisabled = type === 'freetext' || type === 'keyword'
      const updateOperator = value => updateTerm({
        type,
        termIndex,
        value: { ...term, operator: value }
      })
      return (
        <li className={classNames.termRow}>
          <button className={classNames.termButton} type='button' onClick={onEdit} disabled={editDisabled}>
            {renderTermIcon(type)}
            {renderTermValue(type, term, keywordMap)}
          </button>
          {showOperator &&
            <OperatorButton
              className={classNames.operatorButton}
              onChange={updateOperator}
              value={term.operator}
            />
          }
          <button
            className={classNames.queryCloseButton}
            type='button'
            onClick={onRemove}
            title='Remove search term'>
            <Icon icon='close' size={CLOSE_ICON_SIZE} />
          </button>
        </li>
      )
    })
  })
}

function renderTermIcon (type) {
  switch (type) {
    case 'freetext':
      return <Icon className={classNames.termIcon} icon='freetext' size={16} />
    case 'keyword':
      return <Icon className={classNames.termIcon} icon='tag' size={16} />
    case 'camera':
      return <Icon className={classNames.termIcon} icon='camera' size={16} />
    case 'location':
      return <Icon className={classNames.termIcon} icon='marker' size={16} />
    case 'library':
      return <Icon className={classNames.termIcon} icon='library' size={16} />
    case 'date':
      return <Icon className={classNames.termIcon} icon='date' size={16} />
  }
  return null
}

function renderTermValue (type, term, keywordMap) {
  switch (type) {
    case 'freetext':
    case 'camera':
      return term.value
    case 'tags':
      return 'Tag: ' + term.value
    case 'keyword':
      if (keywordMap) {
        return getKeywordName(term.value, keywordMap)
      }
      return 'Loading Keywords…'
    case 'location':
      return term.address || `${term.lat.toFixed(3)}, ${term.lon.toFixed(3)}`
    case 'date':
      if (term.dynamic) {
        return DYNAMIC_ALBUMS[term.dynamic]
      }
      if (term.filterOnly) {
        return 'Timeframe Search'
      }
      if (!term.start && !term.end) {
        return 'Any Date'
      }
      return `${term.start || DEFAULT_START_DATE} - ${term.end || 'Today'}`
    case 'rating':
      const rating = term.reduce((o, r) => {
        o[r.value] = true
        return o
      }, {})
      return <Stars rating={rating} />
    case 'other':
      return 'Advanced'
    case 'dynlink':
      return `Dynamic link: ${term.type}`
    case 'path':
      return `Album: ${(term.value || '').split('/').pop()}`
  }
}

function getKeywordName (id, keywordMap) {
  return keywordMap[id] ? keywordMap[id].name : 'Unknown Keyword'
}

function renderCameras (count, onClick, onRemove) {
  return (
    <li className={classNames.termRow}>
      <button className={classNames.termButton} type='button' onClick={onClick}>
        {renderTermIcon('camera')}
        {pluralize(count, 'camera')}
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={onRemove}
        title='Remove all camera filters'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

function renderLibraries (libraries, onClick, onRemove) {
  return (
    <li className={classNames.termRow}>
      <button className={classNames.termButton} type='button' onClick={onClick}>
        {renderTermIcon('library')}
        Libraries: {libraries
          .map(l => /^virtual_/.test(l.value) ? 'Shared' : titleCase(l.value))
          .join(', ')}
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={onRemove}
        title='Reset library filters'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

function renderExcludes (count, onRemove) {
  return (
    <li className={classNames.termRow}>
      <button className={classNames.termButton} type='button'>
        Excluding {pluralize(count, 'image')}
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={onRemove}
        title='Restore all excluded images'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

function renderIncludes (count, onRemove) {
  return (
    <li className={classNames.termRow}>
      <button className={classNames.termButton} type='button'>
        Including {pluralize(count, 'image')}
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={onRemove}
        title='Remove all included images'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

function renderFixed (count, unFix) {
  return (
    <li className={classNames.termRowDarker}>
      <button className={classNames.termButton} type='button'>
        <Icon className={classNames.termIcon} icon='lock' size={16} />
        Unlock album to modify search
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={() => {
          window.confirm(UNFIX_WARNING) && unFix()
        }}
        title='Unfix album'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

function renderLimit (limit, diversity, onClick, onRemove) {
  return (
    <li className={classNames.termRowDarker}>
      <button className={classNames.termButton} type='button' onClick={onClick}>
        <Icon className={classNames.termIcon} icon='limit' size={16} />
        {limit &&
          `Find Limit: ${limit}`
        }
        {limit && diversity &&
          ', '
        }
        {diversity &&
          `Diversity: ${Math.round(diversity * 100)}%`
        }
      </button>
      <button
        className={classNames.queryCloseButton}
        type='button'
        onClick={onRemove}
        title='Remove find limit'>
        <Icon icon='close' size={CLOSE_ICON_SIZE} />
      </button>
    </li>
  )
}

const ReduxImageSearch = reduxForm({ form: 'query' })(ImageSearch)

const mapStateToProps = ({ search, dynlinks, auth }) => ({
  options: search.options,
  dynlinks: dynlinks.data,
  userHasPerm: perm => userHasPerm(auth.user, perm)
})

const mapDispatchToProps = dispatch => bindActionCreators({
  selectQuery,
  removeQuery,
  addTerm,
  removeTerm,
  editTerm,
  updateTerm,
  updateOperator,
  updateRating,
  fetchSearchOptions,
  unexcludeAllImages,
  unincludeAllImages,
  removeAllCameras,
  resetLibraries,
  fetchDynamicLinks,
  openAdvancedSearch,
  unfixAlbum,
  setRootProperty,
  clearEditingState,
  updateTargetDate
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(ReduxImageSearch)



// WEBPACK FOOTER //
// ./src/components/search/ImageSearch.js